import React, { useState, useEffect, useMemo } from 'react';
import Slider from 'react-slick';
import axios from 'axios';
import EweProductCard from './EweProductCard';
import '../styles/MainCategoriesCarousel.css';

const MainCategoriesCarousel = ({ isLoggedIn, refreshCart, handleCategoryClick }) => {
  const [categoriesWithProducts, setCategoriesWithProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const carouselSettings = useMemo(() => ({
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 5,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 3, slidesToScroll: 3 }
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 2, slidesToScroll: 2 }
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1, slidesToScroll: 1 }
      }
    ]
  }), []);

  // Single API call approach - much more efficient!
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Single API call to get all categories with their products
        const response = await axios.get(`${API_BASE_URL}/ewe/products/categories-with-products`, {
          params: {
            limit: 20 // Configurable number of products per category
          }
        });

        setCategoriesWithProducts(response.data);
        console.log(`Loaded ${response.data.length} categories with products`);

      } catch (err) {
        console.error("Error fetching category data:", err);
        setError(err.response?.data?.message || err.message || 'Failed to load categories');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // Alternative implementation using the optimized endpoint
  const fetchDataOptimized = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the optimized aggregation endpoint for better performance
      const response = await axios.get(`${API_BASE_URL}/ewe/products/categories-with-products-optimized`, {
        params: {
          limit: 20
        }
      });

      setCategoriesWithProducts(response.data);
      console.log(`Loaded ${response.data.length} categories with products (optimized)`);

    } catch (err) {
      console.error("Error fetching category data (optimized):", err);
      setError(err.response?.data?.message || err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Alternative implementation for specific categories only
  const fetchSpecificCategories = async (categoryIds) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`${API_BASE_URL}/ewe/products/categories-with-products/filtered`, {
        categoryIds: categoryIds,
        limit: 20
      });

      setCategoriesWithProducts(response.data);
      console.log(`Loaded ${response.data.length} specific categories with products`);

    } catch (err) {
      console.error("Error fetching specific categories:", err);
      setError(err.response?.data?.message || err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSeeAllClick = useMemo(() => (category) => {
    if (handleCategoryClick) {
      handleCategoryClick({ ...category, type: "main" });
    }
  }, [handleCategoryClick]);

  const handleRetry = () => {
    window.location.reload(); // Simple retry mechanism
  };

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading categories...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>Error loading categories</h3>
          <p>{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (categoriesWithProducts.length === 0) {
    return (
      <div className="no-categories-container">
        <div className="no-categories">
          <h3>No categories found</h3>
          <p>Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-categories-carousel">
      {categoriesWithProducts.map(category => (
        <div key={category.id} className="category-carousel-section">
          <div className="category-header">
            <h2 className="category-title">{category.name}</h2>
          </div>

          {category.products && category.products.length > 0 ? (
            <>
              <Slider {...carouselSettings}>
                {category.products.map(product => (
                  <div key={product.anProductKey || product.productKey} className="carousel-item">
                    <EweProductCard
                      product={product}
                      isLoggedIn={isLoggedIn}
                      refreshCart={refreshCart}
                    />
                  </div>
                ))}
              </Slider>
              <div className="see-all-link">
                <button
                  onClick={() => handleSeeAllClick(category)}
                  aria-label={`See all products in ${category.name}`}
                  className="see-all-button"
                >
                  Vidi sve iz {category.name} ({category.productCount})
                </button>
              </div>
            </>
          ) : (
            <div className="no-products">
              <p>No products available in this category</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default MainCategoriesCarousel;